package info.rezident.app.data.payments

import dev.gitlive.firebase.Firebase
import dev.gitlive.firebase.firestore.firestore
import info.rezident.app.core.Logger
import info.rezident.app.data.PriceVariant
import kotlinx.serialization.Serializable

@Serializable
data class StripePrice(
    val id: String,
    val productId: String,
    val type: String,
    val currency: String,
    val unitAmount: Long,
    val priceVariant: PriceVariant?
)

@Serializable
data class StripePriceMetadata(
    val priceVariant: String? = null
)

@Serializable
private data class FirestorePriceDoc(
    val active: Boolean? = null,
    val type: String? = null,
    val currency: String? = null,
    val unit_amount: Long? = null,
    val metadata: StripePriceMetadata? = null
)

object StripeCatalogRepository {
    private val firestore = Firebase.firestore

    /**
     * Returns all active prices with metadata, optionally filtered by type.
     */
    suspend fun getAllActivePrices(type: String? = null): List<StripePrice> {
        val result = mutableListOf<StripePrice>()
        val products = firestore.collection(StripeCollections.PRODUCTS).get()

        for (product in products.documents) {
            val isProductActive = (product.get("active") as? Boolean) ?: true
            if (!isProductActive) continue

            val prices = product.reference.collection("prices").get()
            for (priceDoc in prices.documents) {
                // Decode entire document into a typed model (including metadata)
                val data = runCatching { priceDoc.data<FirestorePriceDoc>() }.getOrNull()
                if (data == null) {
                    Logger.warn("Skipping price doc ${priceDoc.id}: failed to decode into FirestorePriceDoc")
                    continue
                }

                val isActive = data.active == true
                val priceType = data.type
                if (!isActive) continue
                if (type != null && priceType != type) continue

                val currency = (data.currency ?: "ron").lowercase()
                val unitAmount = data.unit_amount ?: 0L

                val priceVariantString = data.metadata?.priceVariant
                val priceVariant = priceVariantString?.let {
                    try {
                        PriceVariant.valueOf(it.trim().uppercase())
                    } catch (_: IllegalArgumentException) {
                        null
                    }
                }

                // Debug log to trace loaded prices and mapped variants
                Logger.debug(
                    "Stripe price loaded: id=${priceDoc.id}, product=${product.id}, type=${priceType}, currency=${currency}, unitAmount=${unitAmount}, metadata.priceVariant=${priceVariantString}, mapped=${priceVariant}"
                )

                result.add(
                    StripePrice(
                        id = priceDoc.id,
                        productId = product.id,
                        type = priceType ?: "one_time",
                        currency = currency,
                        unitAmount = unitAmount,
                        priceVariant = priceVariant
                    )
                )
            }
        }

        return result
    }

    /**
     * Returns the first active price for a specific price variant and type.
     * Falls back to PriceVariant.A if the requested variant is not found.
     */
    suspend fun getActivePriceForVariant(
        variant: PriceVariant,
        type: String = "one_time"
    ): StripePrice? {
        val allPrices = getAllActivePrices(type)

        // First try to find the exact variant
        val exactMatch = allPrices.firstOrNull { it.priceVariant == variant }
        if (exactMatch != null) return exactMatch

        // Fall back to PriceVariant.B if the requested variant is not found
        Logger.warn("No exact Stripe price found for variant $variant and type=$type. Falling back to A. Available variants: ${allPrices.map { it.priceVariant }}")
        return allPrices.firstOrNull { it.priceVariant == PriceVariant.A }
    }

    /**
     * Returns the first active one-time price id across all active products.
     */
    suspend fun getFirstActiveOneTimePriceId(): String? {
        val products = firestore.collection(StripeCollections.PRODUCTS).get()
        for (product in products.documents) {
            val isProductActive = (product.get("active") as? Boolean) ?: true
            if (!isProductActive) continue

            val prices = product.reference.collection("prices").get()
            val match = prices.documents.firstOrNull { priceDoc ->
                val isActive = (priceDoc.get("active") as? Boolean) == true
                val type = priceDoc.get("type") as? String
                isActive && type == "one_time"
            }
            if (match != null) return match.id
        }
        return null
    }
}